import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLoadingStore } from '@/stores/loading'
import AppLayout from '@/components/layout/AppLayout.vue'
import LoginView from '@/views/LoginView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import EmployeeView from '@/views/EmployeeView.vue'
import DashboardView from '@/views/DashboardView.vue'
import ManageExamsView from '@/views/ManageExamsView.vue'
import ExamResultsView from '@/views/ExamResultsView.vue'
import { Users, ClipboardList, Clock, GraduationCap, TrendingUp, Award, LayoutDashboard, Megaphone } from 'lucide-vue-next'

// Types
interface NavigationItem {
  name: string
  to: string
  icon: any
}

interface RouteMeta {
  requiresAuth?: boolean
  title?: string
  icon?: any
  adminOnly?: boolean
  employeeOnly?: boolean
  hideFromSidebar?: boolean
}

// Route Configuration
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    redirect: '/dashboard', // Default to employee portal dashboard
    children: [
      {
        path: 'dashboard',
        name: 'employee-dashboard',
        component: () => import('@/views/EmployeePortalDashboardView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Dashboard',
          icon: LayoutDashboard,
          employeeOnly: true
        }
      },
      {
        path: 'courses',
        name: 'dashboard',
        component: DashboardView,
        meta: {
          requiresAuth: true,
          title: 'Courses',
          icon: GraduationCap,
          employeeOnly: true
        }
      },
      {
        path: 'employee',
        name: 'employee',
        component: EmployeeView,
        meta: {
          requiresAuth: true,
          title: 'Employees',
          icon: Users,
          adminOnly: true
        }
      },
      {
        path: 'manage-courses',
        name: 'courses',
        component: () => import('@/views/CourseManagementView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Courses',
          icon: GraduationCap,
          adminOnly: true
        }
      },
      {
        path: 'manage-courses/create',
        name: 'course-create',
        component: () => import('@/views/CourseCreateView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Create Course',
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'manage-courses/:id',
        name: 'course-detail',
        component: () => import('@/views/CourseDetailView.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          title: 'Course Details',
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'manage-courses/:id/edit',
        name: 'course-edit',
        component: () => import('@/views/CourseEditView.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          title: 'Edit Course',
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'take-exam/:examId',
        name: 'take-exam',
        component: () => import('@/views/TakeExamView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Take Exam',
          employeeOnly: true
        }
      },
      {
        path: 'lesson-preview/:lessonId',
        name: 'lesson-preview',
        component: () => import('@/views/LessonPreviewView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Lesson Preview',
          employeeOnly: true
        }
      },
      // New course content routes
      {
        path: 'course-content/:courseId',
        name: 'course-content',
        component: () => import('@/views/CourseContentView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Course Content',
          employeeOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'course-complete/:courseId',
        name: 'course-complete',
        component: () => import('@/views/CourseCompleteView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Course Complete',
          employeeOnly: true,
          hideFromSidebar: true
        }
      },
      // TEMPORARILY COMMENTED: My Certificates (employee route)
      // {
      //   path: 'my-certificates',
      //   name: 'my-certificates',
      //   component: () => import('@/views/MyCertificatesView.vue'),
      //   meta: {
      //     requiresAuth: true,
      //     title: 'My Certificates',
      //     icon: Award,
      //     employeeOnly: true
      //   }
      // },
      {
        path: 'company-announcements',
        name: 'employee-announcements',
        component: () => import('@/views/EmployeeAnnouncementsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Announcements',
          icon: Megaphone,
          employeeOnly: true
        }
      },
      {
        path: 'exam',
        name: 'exam',
        component: ManageExamsView,
        meta: {
          requiresAuth: true,
          title: 'Exams',
          icon: ClipboardList,
          adminOnly: true
        }
      },
      {
        path: 'exam/:id',
        name: 'exam-detail',
        component: () => import('@/views/ExamDetailView.vue'),
        meta: {
          requiresAuth: true,
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'exam-results',
        name: 'exam-results',
        component: ExamResultsView,
        meta: {
          requiresAuth: true,
          title: 'Exam Results',
          icon: ClipboardList,
          adminOnly: true
        }
      },
      {
        path: 'training-progress',
        name: 'training-progress',
        component: () => import('@/views/TrainingProgressView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Training Progress',
          icon: TrendingUp,
          adminOnly: true
        }
      },
      {
        path: '/profile',
        name: 'Profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/exam-results/:id',
        name: 'ResultsExamView',
        component: () => import('@/views/ResultsExamView.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'exam-history',
        name: 'exam-history',
        component: () => import('@/views/ExamHistoryView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Exam History',
          icon: Clock,
          employeeOnly: true
        }
      },
      {
        path: '/my-exam-results/:examId/:id',
        name: 'MyExamResultsView',
        component: () => import('@/views/MyExamResultsView.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          title: 'Exam Result Details',
          employeeOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'announcements',
        name: 'announcements',
        component: () => import('@/views/AnnouncementManagementView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Announcements',
          icon: Megaphone,
          adminOnly: true
        }
      },
      {
        path: 'announcements/create',
        name: 'announcement-create',
        component: () => import('@/views/AnnouncementCreateView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Create Announcement',
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'announcements/:id/edit',
        name: 'announcement-edit',
        component: () => import('@/views/AnnouncementEditView.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          title: 'Edit Announcement',
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      // TEMPORARILY COMMENTED: Certificate Template routes
      // {
      //   path: 'certificate-templates',
      //   name: 'certificate-templates',
      //   component: () => import('@/views/CertificateTemplateEditorView.vue'),
      //   meta: {
      //     requiresAuth: true,
      //     title: 'Certificate Template',
      //     icon: Award,
      //     adminOnly: true
      //   }
      // },
      // {
      //   path: 'certificate-templates/:id/edit',
      //   name: 'certificate-template-edit',
      //   component: () => import('@/views/CertificateTemplateEditorView.vue'),
      //   props: true,
      //   meta: {
      //     requiresAuth: true,
      //     title: 'Edit Certificate Template',
      //     adminOnly: true,
      //     hideFromSidebar: true
      //   }
      // }
    ]
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      title: 'Login'
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Forgot Password'
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Reset Password'
    }
  },
  {
    path: '/500',
    name: 'server-error',
    component: () => import('@/views/ServerErrorView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: {
      requiresAuth: false,
      title: 'Not Found'
    }
  }
]

// Router Instance
const router = createRouter({
  history: createWebHistory(process.env.VUE_APP_BASE_URL),
  routes
})

// Navigation Helper
export const getNavigationItems = (routes: RouteRecordRaw[], userType?: string): NavigationItem[] => {
  const layoutRoute = routes.find(route => route.path === '/')
  if (!layoutRoute?.children) return []

  return layoutRoute.children
    .filter(route => {
      const meta = route.meta as RouteMeta
      // Filter out routes that don't need to be in navigation
      if (!meta?.requiresAuth || route.name === 'not-found' || route.path === '/profile') {
        return false
      }
      // Skip routes explicitly hidden from sidebar
      if (meta?.hideFromSidebar) {
        return false
      }
      // Exclude the take-exam, lesson-preview, course-player, and course-complete routes from the sidebar navigation
      if (route.name === 'take-exam' || route.name === 'lesson-preview' || route.name === 'course-content' || route.name === 'course-complete') {
        return false
      }
      // Filter based on user permissions
      if (meta.adminOnly && userType !== 'Admin') {
        return false
      }
      if (meta.employeeOnly && userType !== 'employee') {
        return false
      }
      return true
    })
    .map(route => ({
      name: (route.meta as RouteMeta)?.title || String(route.name),
      to: `/${route.path}`,
      icon: (route.meta as RouteMeta)?.icon
    }))
}

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const loadingStore = useLoadingStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Start loading when navigating to a new page (but not on initial load)
  if (from.name !== undefined) {
    const pageTitle = (to.meta?.title as string) || 'Page'
    loadingStore.startPageLoading(`Loading ${pageTitle}...`)
  }

  // Always check authentication status first
  const isAuthenticated = await authStore.checkAuth()
  const userType = authStore.currentUser?.user_type_label

  // If inside an iframe and already authenticated, always redirect to the login page
  // so it can show the "already signed in" message — never load portal pages inside
  // the iframe (prevents dashboard from rendering inside iframe on refresh)
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top
  if (isInIframe && isAuthenticated && to.name !== 'login') {
    loadingStore.stopPageLoading()
    next({ name: 'login' })
    return
  }

  // If user is authenticated and trying to access login page, redirect to home
  if (to.name === 'login' && isAuthenticated) {
    loadingStore.stopPageLoading()
    
    // When inside an iframe, stay on the login page so it can show
    // an "already logged in" message instead of loading the portal
    // inside the iframe
    if (isInIframe) {
      next()
      return
    }
    
    // Redirect admins to employee page, employees to employee portal dashboard
    if (userType === 'Admin') {
      next({ name: 'employee' })
    } else {
      next({ name: 'employee-dashboard' })
    }
    return
  }

  // Check if route requires authentication
  if (requiresAuth && !isAuthenticated) {
    loadingStore.stopPageLoading()
    // Redirect to login if not authenticated
    next({ name: 'login' })
    return
  }

  // Check if route is admin-only and user is not admin
  const routeMeta = to.meta as RouteMeta
  if (routeMeta?.adminOnly && isAuthenticated) {
    if (userType !== 'Admin') {
      loadingStore.stopPageLoading()
      // Redirect non-admin users to employee portal dashboard
      next({ name: 'employee-dashboard' })
      return
    }
  }

  // Check if route is employee-only and user is not employee
  if (routeMeta?.employeeOnly && isAuthenticated) {
    if (userType !== 'employee') {
      loadingStore.stopPageLoading()
      // Redirect non-employee users (admins) to employee management page
      next({ name: 'employee' })
      return
    }
  }

  // Handle root path redirect based on user type
  if (to.path === '/' && isAuthenticated) {
    loadingStore.stopPageLoading()
    // Redirect admins to employee page, employees to employee portal dashboard
    if (userType === 'Admin') {
      next({ name: 'employee' })
    } else {
      next({ name: 'employee-dashboard' })
    }
    return
  }

  // Handle non-admin users accessing employee page (default redirect) - redirect to employee portal dashboard
  if (to.name === 'employee' && isAuthenticated && (from.name === 'login' || from.path === '/' || from.name === undefined)) {
    if (userType !== 'Admin') {
      loadingStore.stopPageLoading()
      next({ name: 'employee-dashboard' })
      return
    }
  }

  next()
})

router.afterEach(() => {
  const loadingStore = useLoadingStore()
  // Add a small delay to show the loader briefly for better UX
  setTimeout(() => {
    loadingStore.stopPageLoading()
  }, 500)
})

export default router